import DirectionsWalkRounded from '@mui/icons-material/DirectionsWalkRounded';
import SaveIcon from '@mui/icons-material/Save';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import { useEffect, useState } from 'react';

import { useGetMailRoomByIdQuery } from '@api/mailroom';
import { useUpdatePiecesMutation } from '@api/piece';
import { PieceAction } from '@apiContract';
import { PAST_TIME_ERROR, type PickUpDate, PickupAction } from '@components/MailpieceActions/pick-up/PickupAction';
import { useUserPermissions } from '@hooks/useUserPermissions';
import { Roles } from '@rdTypes/shared';
import LabelSwitch from 'src/components/Inputs/LabelSwitch';

import { showApiErrorToast } from '@utils/apiErrorToast';
import { nowAsWallClock, wallClockToInstant } from '@utils/format-time';
import { browserTimeZone } from '@utils/timeZones';

import { ActionComponentProps } from '../../index';
import { isPickUpLessCurrentDate } from '../../utils';
import { CollapsibleOptions } from './CollapsibleOptions';
import { buildUpdatePiecesRequest } from './helper';

const AMOUNT_DAYS_OF_WEEK = 7;

export const RequestPickUp = ({ mailpieces, actionStarted, onActionFinish, isForce }: ActionComponentProps) => {
  const { isAllowed } = useUserPermissions();
  const isAdmin = isAllowed([Roles.Admin]);
  const [updatePieces] = useUpdatePiecesMutation();
  const piecesQuantity = mailpieces.length;

  const { data: mailRoom } = useGetMailRoomByIdQuery(mailpieces[0].mailroomId, {
    skip: !piecesQuantity,
  });

  const mailroomTimeZone = mailRoom?.hoursOfOperation.timeZoneInfoId || browserTimeZone;

  const [actionInProgress, setActionInProgress] = useState<boolean>(false);
  const [pickUpDate, setPickUpDate] = useState<PickUpDate>(null);
  const [error, setError] = useState('');
  const [shouldNotifyMailroom, setShouldNotifyMailroom] = useState(false);
  const [skipNotifyRecipient, setSkipNotifyRecipient] = useState(true);

  useEffect(() => {
    if (actionStarted) {
      const activeSpans = mailRoom?.hoursOfOperation.activeSpans || [];
      const date = nowAsWallClock(mailroomTimeZone);
      if (activeSpans.length) {
        const sortedSpans = [...activeSpans].sort((spanA, spanB) => spanA.dayOfWeek - spanB.dayOfWeek);

        const activeSpan = sortedSpans.find((span) => span.dayOfWeek >= date.getDay()) || activeSpans[0];

        const daysToClosestSpan =
          activeSpan.dayOfWeek >= date.getDay()
            ? activeSpan.dayOfWeek - date.getDay()
            : AMOUNT_DAYS_OF_WEEK - date.getDay() + activeSpan.dayOfWeek;

        date.setDate(date.getDate() + daysToClosestSpan);
      }

      setPickUpDate(date);
      setError('');
    }
  }, [actionStarted, mailRoom, mailroomTimeZone]);

  const onChangeValue = (date: PickUpDate) => setPickUpDate(date);
  const onChangeError = setError;

  const submit = async () => {
    if (error || !pickUpDate) return;

    if (isPickUpLessCurrentDate(pickUpDate, mailroomTimeZone)) {
      setError(PAST_TIME_ERROR);
      return;
    }

    try {
      setActionInProgress(true);

      const UTCPickUpDate = wallClockToInstant(pickUpDate, mailroomTimeZone).toISOString();

      const requestData = buildUpdatePiecesRequest({
        mailpieces,
        action: PieceAction.RequestPickUp,
        isForce,
        shouldNotifyMailroom,
        shouldNotifyRecipient: !skipNotifyRecipient,
        additionalData: {
          pickupDate: UTCPickUpDate,
        },
      });

      await updatePieces(requestData).unwrap();

      onActionFinish();
    } catch (err) {
      showApiErrorToast(err);
    } finally {
      setActionInProgress(false);
    }
  };

  return (
    <Dialog slotProps={{ paper: { sx: { width: 450 } } }} open={actionStarted} onClose={onActionFinish}>
      <DialogTitle>
        {piecesQuantity && piecesQuantity > 1 ? 'Request pickup for selected items' : 'Request pickup'}
      </DialogTitle>
      <DialogContent>
        <PickupAction
          mailRoom={mailRoom}
          mailroomTimeZone={mailroomTimeZone}
          pickUpDate={pickUpDate}
          onChangeValue={onChangeValue}
          error={error}
          onChangeError={onChangeError}
          piecesQuantity={piecesQuantity}
        />

        {isForce && (
          <CollapsibleOptions>
            <Box sx={{ mt: 1 }}>
              <LabelSwitch
                checked={shouldNotifyMailroom}
                onChange={() => setShouldNotifyMailroom(!shouldNotifyMailroom)}
                label="Notify mailroom users about pending request"
              />
            </Box>
            {isAdmin && (
              <Box sx={{ mt: 1 }}>
                <LabelSwitch
                  checked={skipNotifyRecipient}
                  onChange={() => setSkipNotifyRecipient(!skipNotifyRecipient)}
                  label="Skip notification to recipient about scheduled pickup"
                />
              </Box>
            )}
          </CollapsibleOptions>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onActionFinish} color="inherit" disabled={actionInProgress}>
          Cancel
        </Button>
        <Button
          onClick={submit}
          loading={actionInProgress}
          startIcon={actionInProgress && <SaveIcon />}
          loadingPosition={actionInProgress ? 'start' : undefined}
          variant="contained"
          color="primary"
        >
          Submit
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export const requestPickUpConfig = {
  title: 'Pick Up',
  Icon: DirectionsWalkRounded,
};
